/**
 * Kleiner, typisierter Fetch-Client für das zuep-gateway (`/api/*`).
 *
 * Alle Aufrufe verlangen ein Bearer-Token, das der aufrufende Component
 * über `useKeycloak()` (siehe `src/App.tsx`) besorgt und hier übergibt --
 * dieses Modul kennt Keycloak selbst nicht, damit es auch mit dem
 * `REACT_APP_SKIP_AUTH`-Mock aus `src/index.tsx` funktioniert.
 *
 * Base-URL: `REACT_APP_GATEWAY_URL` (z.B. `https://zuep-gateway.example.at`),
 * Default `/` (gleicher Origin wie die App, z.B. hinter einem Reverse-Proxy).
 */

const RAW_BASE_URL = process.env.REACT_APP_GATEWAY_URL || '/';
const BASE_URL = RAW_BASE_URL.endsWith('/') ? RAW_BASE_URL.slice(0, -1) : RAW_BASE_URL;

export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Demo-Modus (🧪): Server prüft alles echt, persistiert aber nichts und
 * übermittelt nichts an die Behörde. Hot-Modus (🔥, nur Admins, server-
 * seitig verifiziert): Aktionen sofort ausführen, Zeitfenster übersteuert. */
export function isDemoMode(): boolean {
  return localStorage.getItem('zuep-demo') === '1';
}
export function isHotMode(): boolean {
  return localStorage.getItem('zuep-hot') === '1';
}
export function setDemoMode(on: boolean): void {
  localStorage.setItem('zuep-demo', on ? '1' : '0');
}
export function setHotMode(on: boolean): void {
  localStorage.setItem('zuep-hot', on ? '1' : '0');
}
/** Admin-Ansicht als anderes Mitglied (nur lesend; Server erzwingt GET+Admin). */
export function getViewAs(): string {
  return localStorage.getItem('zuep-view-as') || '';
}
export function setViewAs(mgdNummer: string): void {
  if (mgdNummer) localStorage.setItem('zuep-view-as', mgdNummer);
  else localStorage.removeItem('zuep-view-as');
}

function applyModeHeaders(headers: Headers): void {
  if (isDemoMode()) headers.set('X-Demo', '1');
  if (isHotMode()) headers.set('X-Hot', '1');
  const viewAs = getViewAs();
  if (viewAs) headers.set('X-Als-Mitglied', viewAs);
}

/** Access-Token vor dem Request auffrischen, wenn er in <60 s abläuft.
 * Ohne das scheiterte JEDER Call nach Ablauf der Token-Lebensdauer mit 401
 * („ungültiger Token") — wer das Antragsformular länger ausfüllte, konnte
 * nicht mehr absenden (Fall Fabbricotti, 08.09.2026). Der Refresh nutzt die
 * Keycloak-SSO-Session (lebt deutlich länger als der Access-Token); ist auch
 * die abgelaufen, läuft der Call wie bisher auf 401. */
async function frischesToken(fallback: string | undefined): Promise<string | undefined> {
  try {
    const kc = (await import('../keycloak')).default;
    if (kc.authenticated) {
      await kc.updateToken(60);
      return kc.token || fallback;
    }
  } catch {
    // Refresh fehlgeschlagen (SSO-Session abgelaufen) -- mit altem Token
    // weiter, der 401 unten liefert dann die ehrliche Fehlermeldung.
  }
  return fallback;
}

export async function apiFetch<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  applyModeHeaders(headers);
  const tok = await frischesToken(token);
  if (tok) headers.set('Authorization', `Bearer ${tok}`);

  let response = await fetch(apiUrl(path), { ...init, headers });

  // GETs bei 5xx EINMAL automatisch wiederholen: deckt transiente Ausfälle
  // ab (Gateway-Neustart, Proxy-Timeout) -- gemeldet als "Zurück -> 504".
  const method = (init.method || 'GET').toUpperCase();
  if (!response.ok && response.status >= 500 && method === 'GET') {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    response = await fetch(apiUrl(path), { ...init, headers });
  }

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.detail || JSON.stringify(body);
    } catch {
      // Response war kein JSON -- Statustext reicht als Fehlermeldung.
    }
    if (response.status === 401) {
      detail = 'Deine Anmeldung ist abgelaufen — bitte lade die Seite neu und melde dich erneut an.';
    }
    throw new ApiError(response.status, detail || response.statusText || `HTTP ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

// ---- Typen: GET /api/me ------------------------------------------------

export interface Person {
  p_id: number;
  vorname: string;
  famname: string;
  gebname?: string | null;
  geschlecht: number;
  gebdatum: string;
  geb_ort: string;
  geb_ort_plz?: string | null;
  geb_ort_land?: string | null;
  vorname_m: string;
  vorname_v: string;
  staat_1: string;
  staat_2?: string | null;
  kontakt_tel: string;
  kontakt_mail: string;
  suep?: string | null;
  [key: string]: unknown;
}

export interface Adresse {
  a_id: number;
  p_id: number;
  art: number;
  land?: string | null;
  plz?: string | null;
  ort: string;
  strasse: string;
  zusatz: string;
  von: string;
  bis?: string | null;
}

export interface Beschaeftigung {
  b_id: number;
  p_id: number;
  art: number;
  name: string;
  info: string;
  von: string;
  bis?: string | null;
}

export interface Dokinfo {
  d_id: number;
  p_id: number;
  art: number;
  ausstellung: string;
  dokname?: string | null;
  mimetyp: string;
  nummer?: string | null;
  info?: string | null;
}

export interface ZuepStatus {
  p_id: number;
  n_status?: number | null;
  n_frist?: string | null;
  e_status?: number | null;
  e_frist?: string | null;
  [key: string]: unknown;
}

/** Offenliste: leeres Objekt (bzw. Objekt ohne Zusatzkeys über `p_id` hinaus) = nichts offen. */
export type OffenListe = Record<string, unknown>;

/** Abgeleiteter, mitglieder-freundlicher ZÜP-Status (vom Gateway, app/member_status.py). */
export interface ZuepStatusInfo {
  code: 'zuverlaessig' | 'in_bearbeitung' | 'angehalten' | 'unzuverlaessig' | 'aufgehoben' | 'offen' | 'keine';
  label: string;
  severity: 'ok' | 'pending' | 'warn' | 'error' | 'none';
  frist?: string | null;
  open?: string[];
}

/** Flughafenausweis-Daten der Ausweisstelle (monatlicher Import).
 * `stand` = Datum des Datenstands — die Daten sind NICHT tagesaktuell,
 * das gehört bei jeder Anzeige dazu. */
export interface AusweisInfo {
  p_id: number;
  gueltig_bis?: string | null;
  schulung?: string | null;
  status?: string | null;
  info?: string | null;
  stand?: string | null;
}

export interface MeResponse {
  p_id: number;
  person: Person;
  adressen: Adresse[];
  beschaeftigung: Beschaeftigung[];
  dokinfo: Dokinfo[];
  status: ZuepStatus;
  offen: OffenListe;
  freigaben?: unknown[];
  ausweis_info?: AusweisInfo | null;
  zuep_status?: ZuepStatusInfo;
}

export async function getMe(token: string | undefined): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/me', token);
}

/** Lädt ein neues Dokument (PDF/JPEG/PNG, max. 15 MB) in die Prüf-Queue des
 * Gateways hoch. Bewusst Raw-Body statt multipart (Gateway hat keine
 * multipart-Dependency); Dateiname/Notiz gehen URL-encodiert über Header.
 * Ein Dokument-DOWNLOAD existiert absichtlich nicht mehr: Mitglieder sehen
 * nur Metadaten (`dokinfo`), nie den Scan selbst (Sicherheitsentscheidung
 * 2026-07-06 — bei gekapertem Konto wäre der Ausweis-Scan sonst abgreifbar). */
export async function uploadDokument(
  token: string | undefined,
  file: File,
  note?: string,
): Promise<{ ok: boolean; id: number }> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  headers.set('X-Filename', encodeURIComponent(file.name));
  applyModeHeaders(headers);
  if (note) headers.set('X-Note', encodeURIComponent(note));
  const tok = await frischesToken(token);
  if (tok) headers.set('Authorization', `Bearer ${tok}`);
  const response = await fetch(apiUrl('/api/me/dokument'), {
    method: 'POST',
    headers,
    body: file,
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.detail || '';
    } catch {
      // kein JSON -- Statustext reicht.
    }
    throw new ApiError(response.status, detail || response.statusText || `HTTP ${response.status}`);
  }
  return response.json();
}

// ---- Antrags-Einreichung über das Portal ---------------------------------

export type AntragArt = 'verlaengerung' | 'erstantrag' | 'ausweis';

/** Reicht den Antrag (ZIP: zuep.json + Dokumente + PDFs) verbindlich ein.
 * Die beiden Bestätigungen (Richtigkeit + Kostenhinweis) werden vom Gateway
 * SERVER-seitig verlangt — dieser Client sendet sie nur, wenn der Aufrufer
 * sie explizit übergibt (UI-Checkboxen). */
export async function submitAntrag(
  token: string | undefined,
  zipBlob: Blob,
  art: AntragArt,
  filename: string,
  bestaetigt: { richtig: boolean; kosten: boolean },
): Promise<{ ok: boolean; id: number }> {
  const flags = [bestaetigt.richtig && 'richtig', bestaetigt.kosten && 'kosten']
    .filter(Boolean)
    .join(',');
  const headers = new Headers();
  headers.set('Content-Type', 'application/zip');
  headers.set('X-Antrag-Art', art);
  headers.set('X-Bestaetigung', flags);
  headers.set('X-Filename', encodeURIComponent(filename));
  applyModeHeaders(headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(apiUrl('/api/me/antrag'), { method: 'POST', headers, body: zipBlob });
  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.detail || '';
    } catch {
      // kein JSON
    }
    throw new ApiError(response.status, detail || response.statusText || `HTTP ${response.status}`);
  }
  return response.json();
}

// ---- Rechte + Selbst-Freigabe ----------------------------------------------

/** Portal-Rechte des eingeloggten Users. Die Admin-Eigenschaft wird im
 * Gateway verwaltet (ADMIN_USERS-Env + admin_user-Tabelle) — NICHT über
 * Keycloak-Rollen; der Verein hat dort keinen Admin-Zugriff. */
export async function getRechte(token: string | undefined): Promise<{ admin: boolean; username: string }> {
  return apiFetch('/api/me/rechte', token);
}

export interface FreigabeAuftrag {
  name?: string | null;
  id: number;
  mgd_nummer: string;
  p_id?: number | null;
  info?: string | null;
  earliest_at?: string | null;
  status: string;
  created_at?: string | null;
  executed_at?: string | null;
  f_id?: number | null;
  note?: string | null;
  antrag_id?: number | null;
}

/** Ein-Klick-Verlängerung: plant die automatische Freigabe im optimalen
 * Fenster. Alle drei Bestätigungen werden server-seitig erzwungen. */
export async function selbstFreigabe(
  token: string | undefined,
  bestaetigt: { unveraendert: boolean; richtig: boolean; kosten: boolean },
): Promise<{ ok: boolean; id: number; geplant_fuer: string; uebermittlung_fruehestens: string }> {
  const flags = [
    bestaetigt.unveraendert && 'unveraendert',
    bestaetigt.richtig && 'richtig',
    bestaetigt.kosten && 'kosten',
  ]
    .filter(Boolean)
    .join(',');
  return apiFetch('/api/me/selbst-freigabe', token, {
    method: 'POST',
    headers: { 'X-Bestaetigung': flags },
  });
}

export async function getSelbstFreigabe(
  token: string | undefined,
): Promise<{ auftrag: FreigabeAuftrag | null }> {
  return apiFetch('/api/me/selbst-freigabe', token);
}

export async function stornoSelbstFreigabe(token: string | undefined): Promise<{ ok: boolean }> {
  return apiFetch('/api/me/selbst-freigabe', token, { method: 'DELETE' });
}

// ---- Admin: Selbst-Freigabe-Aufträge + Admin-Verwaltung ---------------------

export interface CockpitBehoerde {
  p_id: string;
  behoerden_status?: string;
  mgd_nummer: string;
  name: string;
  f_id: number;
  vom: string;
  deadline: string | null;
  resttage: number | null;
  e_frist: string | null;
}

export interface CockpitFaellig {
  p_id: string;
  telefon?: string;
  mgd_nummer: string;
  name: string;
  e_frist: string;
  tage: number;
  vorgang: string;
  ampel: string;
  braucht_sr?: boolean;
  sr_laender?: string[];
}

export async function flughafenPdfs(
  token: string | undefined,
  daten: Record<string, string>,
): Promise<{ antrag: string; anlage4b: string; signiert_von: string; datum: string }> {
  return apiFetch('/api/me/flughafen-pdfs', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(daten),
  });
}

export async function flughafenSenden(
  token: string | undefined,
  daten: Record<string, string>,
): Promise<{ ok: boolean; gesendet_an: string; cc?: string | null; demo?: boolean }> {
  return apiFetch('/api/me/flughafen-senden', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(daten),
  });
}

// ---- Strafregisterauszug Italien (Procura Bozen) -------------------------

export interface StrafregisterItDaten {
  vorname: string;
  famname: string;
  gebdatum: string;
  geb_ort: string;
  geb_land: string;
  geschlecht: string;
  wohnsitzgemeinde: string;
  steuernummer_vorschlag: string | null;
}

export interface StrafregisterItInfo {
  relevant: boolean;
  daten: StrafregisterItDaten;
  ausweis: { dokname: string | null; art: number; ausstellung: string | null }[];
  procura: string;
}

export async function getStrafregisterIt(token: string | undefined): Promise<StrafregisterItInfo> {
  return apiFetch('/api/me/strafregister-it', token);
}

/** Erzeugt das befüllte Formblatt Nr. 3 als PDF zum DOWNLOAD — versendet
 * wird bewusst NICHTS: Das Mitglied schickt den Antrag selbst an die Procura
 * (Adresse + fertiger Mailtext kommen in der Antwort mit). */
export async function strafregisterItPdf(
  token: string | undefined,
  daten: { steuernummer: string; wohnsitzgemeinde?: string; unterschrift_png?: string | null },
): Promise<{ ok: boolean; dateiname: string; pdf: string; procura: string; mailtext: string }> {
  return apiFetch('/api/me/strafregister-it/pdf', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(daten),
  });
}

export interface CockpitFertig {
  p_id: string;
  mgd_nummer: string;
  name: string;
  f_id: number;
  vom: string;
  abgeschlossen?: string | null;
  e_frist?: string | null;
}
export interface LetzterKontakt {
  art: string;               // telefonat | versuch | email | aufforderung
  at: string;
  actor?: string | null;
  note?: string | null;
}
export interface CockpitAbgelaufen {
  p_id: string;
  mgd_nummer: string;
  name: string;
  e_frist?: string | null;   // USP löscht die Frist nach Ablauf
  nv: boolean;
  vorgang?: string;          // z.B. 'bei Behörde – in Bearbeitung', '' = kein Vorgang
  telefon?: string;
  email_vorhanden?: boolean;
  letzte_aufforderung?: string | null;
  letzter_kontakt?: LetzterKontakt | null;
}

export type KontaktArt = 'telefonat' | 'versuch' | 'email';

export async function logKontakt(
  token: string | undefined,
  pId: string | number,
  art: KontaktArt,
  note?: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/abgelaufen/${pId}/kontakt`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ art, note: note || null }),
  });
}
export async function getCockpit(
  token: string | undefined,
): Promise<{ behoerde: CockpitBehoerde[]; faellig: CockpitFaellig[]; fertig?: CockpitFertig[]; abgelaufen?: CockpitAbgelaufen[]; fristen?: Record<string, string>; stand: string }> {
  return apiFetch('/api/admin/cockpit', token);
}

export async function sendeAbgelaufenAufforderung(
  token: string | undefined,
  pId: string | number,
): Promise<{ ok: boolean; gesendet_an: string }> {
  return apiFetch(`/api/admin/abgelaufen/${pId}/aufforderung`, token, { method: 'POST' });
}

export async function listFreigabeAuftraege(
  token: string | undefined,
  status = 'geplant',
): Promise<{ auftraege: FreigabeAuftrag[] }> {
  return apiFetch(`/api/admin/freigabe-auftraege?status=${encodeURIComponent(status)}`, token);
}

export async function bestaetigeFreigabeAuftrag(
  token: string | undefined,
  id: number,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/freigabe-auftraege/${id}/bestaetigen`, token, { method: 'POST' });
}

export interface AuditEintrag {
  actor_name?: string | null;
  name?: string | null;
  id: number;
  at?: string | null;
  actor?: string | null;
  action?: string | null;
  mgd_nummer?: string | null;
  p_id?: number | null;
  detail?: string | null;
}

export async function getAudit(
  token: string | undefined,
  limit = 200,
): Promise<{ eintraege: AuditEintrag[] }> {
  return apiFetch(`/api/admin/audit?limit=${limit}`, token);
}

/** Führt einen geplanten Auftrag SOFORT aus (vorzeitig; atomarer Claim im
 * Gateway verhindert Doppel-Ausführung durch den Scheduler). */
export async function freigabeAuftragJetzt(
  token: string | undefined,
  id: number,
): Promise<{ ok: boolean; demo?: boolean; freigabe: Record<string, unknown> }> {
  return apiFetch(`/api/admin/freigabe-auftraege/${id}/jetzt`, token, { method: 'POST' });
}

export async function stornoFreigabeAuftrag(
  token: string | undefined,
  id: number,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/freigabe-auftraege/${id}/storno`, token, { method: 'POST' });
}

export interface MitgliedEintrag {
  mgd_nummer: string;
  p_id?: number | null;
  name?: string | null;
}

export async function listMitglieder(
  token: string | undefined,
): Promise<{ mitglieder: MitgliedEintrag[] }> {
  return apiFetch('/api/admin/mitglieder', token);
}

export interface BlockEintrag {
  mgd_nummer: string;
  grund?: string | null;
  name?: string | null;
  added_by?: string | null;
}

export async function listBlockierte(
  token: string | undefined,
): Promise<{ blockierte: BlockEintrag[] }> {
  return apiFetch('/api/admin/blockierte', token);
}

export async function addBlockiert(
  token: string | undefined,
  mgdNummer: string,
  grund: string,
): Promise<{ ok: boolean }> {
  return apiFetch('/api/admin/blockierte', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mgd_nummer: mgdNummer, grund }),
  });
}

export async function removeBlockiert(
  token: string | undefined,
  mgdNummer: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/blockierte/${encodeURIComponent(mgdNummer)}`, token, { method: 'DELETE' });
}

export interface ErinnerungsBlock {
  mgd_nummer: string;
  name?: string | null;
  seit?: string | null;
  notiz?: string | null;
  typ?: 'optout' | 'auto' | string;
}

export interface Foerderndes {
  mgd_nummer: string;
  name?: string | null;
  status?: string | null;
}

export async function listErinnerungsBlocks(
  token: string | undefined,
): Promise<{ blocks: ErinnerungsBlock[]; foerdernde?: Foerderndes[]; weitere_inaktive?: number }> {
  return apiFetch('/api/admin/erinnerungs-block', token);
}

export async function setErinnerungsBlock(
  token: string | undefined,
  mgdNummer: string,
): Promise<{ ok: boolean }> {
  return apiFetch('/api/admin/erinnerungs-block', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mgd_nummer: mgdNummer }),
  });
}

export async function removeErinnerungsBlock(
  token: string | undefined,
  mgdNummer: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/erinnerungs-block/${encodeURIComponent(mgdNummer)}`, token, { method: 'DELETE' });
}

export interface AdminEintrag {
  mgd_nummer: string;
  added_by?: string | null;
  added_at?: string | null;
}

export async function listAdmins(
  token: string | undefined,
): Promise<{ admins: AdminEintrag[]; env_admins: string[] }> {
  return apiFetch('/api/admin/admins', token);
}

export async function addAdmin(token: string | undefined, mgdNummer: string): Promise<{ ok: boolean }> {
  return apiFetch('/api/admin/admins', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mgd_nummer: mgdNummer }),
  });
}

export async function removeAdmin(token: string | undefined, mgdNummer: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/admins/${encodeURIComponent(mgdNummer)}`, token, { method: 'DELETE' });
}

// ---- Admin: Antrags-Queue -------------------------------------------------

export interface PendingAntrag {
  name?: string | null;
  id: number;
  mgd_nummer: string;
  p_id?: number | null;
  art: AntragArt | string;
  filename?: string | null;
  groesse?: number | null;
  created_at?: string | null;
  status: string;
  note?: string | null;
}

export async function listAntraege(
  token: string | undefined,
  status = 'neu',
): Promise<{ antraege: PendingAntrag[] }> {
  return apiFetch(`/api/admin/antraege?status=${encodeURIComponent(status)}`, token);
}

export async function downloadAntragZip(token: string | undefined, id: number): Promise<Blob> {
  const headers = new Headers();
  const tok = await frischesToken(token);
  if (tok) headers.set('Authorization', `Bearer ${tok}`);
  const response = await fetch(apiUrl(`/api/admin/antraege/${id}/zip`), { headers });
  if (!response.ok) throw new ApiError(response.status, response.statusText);
  return response.blob();
}

// ---- Dokument-Prüf-Queue (Nachreich-Kachel + aus Mails übernommene) ------

export interface PendingDokument {
  id: number;
  mgd_nummer: string;
  name?: string | null;
  p_id?: number | null;
  filename: string;
  mimetyp?: string | null;
  note?: string | null;
  status: string;
  uploaded_at?: string | null;
}

export async function listPendingDokumente(
  token: string | undefined,
  status = 'neu',
): Promise<{ dokumente: PendingDokument[] }> {
  return apiFetch(`/api/admin/dokumente?status=${encodeURIComponent(status)}`, token);
}

export async function downloadPendingDokument(token: string | undefined, id: number): Promise<Blob> {
  const headers = new Headers();
  const tok = await frischesToken(token);
  if (tok) headers.set('Authorization', `Bearer ${tok}`);
  const response = await fetch(apiUrl(`/api/admin/dokumente/${id}`), { headers });
  if (!response.ok) throw new ApiError(response.status, response.statusText);
  return response.blob();
}

export async function setPendingDokumentStatus(
  token: string | undefined,
  id: number,
  status: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/dokumente/${id}/status`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

export interface DokumentUebernahme {
  art: number;
  nummer?: string | null;
  ausstellung: string;
  gueltig_bis?: string | null;
  strafreg?: number;
  land?: string | null;
}

export async function uebernehmePendingDokument(
  token: string | undefined,
  id: number,
  daten: DokumentUebernahme,
): Promise<{ ok: boolean; d_id: number }> {
  return apiFetch(`/api/admin/dokumente/${id}/uebernehmen`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(daten),
  });
}

export async function setAntragStatus(
  token: string | undefined,
  id: number,
  status: string,
  note?: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/antraege/${id}/status`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, note: note ?? null }),
  });
}

/** Erteilt die (kostenpflichtige) Freigabe — Guards laufen im Gateway. */
export async function antragFreigeben(
  token: string | undefined,
  id: number,
  opts: { info?: string; forceZeitfenster?: boolean } = {},
): Promise<{ ok: boolean; geplant?: boolean; uebermittelt?: boolean; uebermittlung_fruehestens?: string; freigabe?: Record<string, unknown> }> {
  return apiFetch(`/api/admin/antraege/${id}/freigabe`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      info: opts.info ?? 'Pilot',
      force_zeitfenster: opts.forceZeitfenster ?? false,
    }),
  });
}

/** Legt einen Erstantrag im USP an (= „übermitteln", NICHT freigeben). Kostenneutral;
 *  liefert die neu vergebene p_id. Nur für Erstanträge ohne bestehende Verknüpfung. */
export async function antragUebernehmen(
  token: string | undefined,
  id: number,
): Promise<{ ok: boolean; demo?: boolean; p_id?: number; offen?: string[] }> {
  return apiFetch(`/api/admin/antraege/${id}/uebernehmen`, token, { method: 'POST' });
}

export interface AntragDokument {
  art?: number;
  nummer?: string | null;
  ausstellung?: string | null;
  name?: string | null;
  mime?: string | null;
}

export interface AntragAusweis {
  person: Record<string, unknown>;
  dokument: { art?: number; nummer?: string; ausstellung?: string } | null;
  bild_base64: string | null;
  bild_mime: string | null;
  dokname: string | null;
  // Vollständige Prüf-Ansicht: alle Dokumente + Nummern, alle Bilder,
  // PDF-Liste und Änderungsprotokoll (auch für Verlängerungen).
  dokumente?: AntragDokument[];
  bilder?: { name: string; mime: string; b64: string }[];
  pdfs?: string[];
  aenderungen?: string | null;
}

/** Identitätsdaten + Dokumente/Bilder aus dem Antrag-ZIP für die Sichtprüfung. */
export async function getAntragAusweis(
  token: string | undefined,
  id: number,
): Promise<AntragAusweis> {
  return apiFetch(`/api/admin/antraege/${id}/ausweis`, token);
}

/** Einzelne Antrags-Datei (z.B. Strafregister-PDF) als Blob — für „in neuem Tab öffnen". */
export async function getAntragDatei(
  token: string | undefined,
  id: number,
  name: string,
): Promise<Blob> {
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const resp = await fetch(apiUrl(`/api/admin/antraege/${id}/datei?name=${encodeURIComponent(name)}`), { headers });
  if (!resp.ok) throw new ApiError(resp.status, resp.statusText);
  return resp.blob();
}

// ---- Typen: GET /api/admin/matching -------------------------------------

export interface MatchCandidate {
  p_id: number;
  score: number;
  famname: string | null;
  vorname: string | null;
}

export interface MatchingReviewEntry {
  mgd_nummer: string;
  famname: string;
  vorname: string;
  status: 'candidate' | 'unmatched' | string;
  candidates: MatchCandidate[];
}

export interface MatchingResponse {
  blockiert?: number;
  verknuepft?: number;
  auto: number;
  candidates: number;
  unmatched: number;
  review: MatchingReviewEntry[];
}

export async function getMatching(token: string | undefined): Promise<MatchingResponse> {
  return apiFetch<MatchingResponse>('/api/admin/matching', token);
}

export interface ConfirmMatchResponse {
  ok: boolean;
}

/** `pId === null` bedeutet "kein Treffer" (siehe zuep-gateway/app/routes_admin.py). */
export async function confirmMatch(
  token: string | undefined,
  mgdNummer: string,
  pId: number | null,
): Promise<ConfirmMatchResponse> {
  return apiFetch<ConfirmMatchResponse>('/api/admin/matching/confirm', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mgd_nummer: mgdNummer, p_id: pId }),
  });
}
